import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { campanasApi } from '../api/campanas.api';
import { metricasApi } from '../api/metricas.api';

// Tablero de KPIs (16/09/2026, pedido de Javier): mensajes, tasa de respuesta,
// tasa de bloqueo y pendientes de contactar. Son números-hero, no un gráfico —
// no hace falta más que eso para leerlos de un vistazo. El filtro de campaña
// afecta TODAS las tarjetas (mismo patrón que el filtro de la bandeja).
export function MetricasAdminPage(): JSX.Element {
  const es = useI18n();
  const t = es.admin.metricas;
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [campana, setCampana] = useState('');

  const campanasQuery = useQuery({ queryKey: qk.campanas.all, queryFn: campanasApi.list, staleTime: 60_000 });

  const recontacto = useQuery({ queryKey: qk.admin.recontacto, queryFn: metricasApi.recontacto });
  const { data, isLoading, error } = useQuery({
    queryKey: qk.admin.metricas({ desde, hasta, campana }),
    queryFn: () =>
      metricasApi.obtener({
        ...(desde ? { desde } : {}),
        ...(hasta ? { hasta } : {}),
        ...(campana ? { campana } : {}),
      }),
  });

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="font-titulos text-3xl font-bold text-text-marca">{t.titulo}</h1>
      <p className="mt-1 max-w-3xl text-sm text-text-subtle">{t.intro}</p>

      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md border border-marca-neutro bg-surface p-3">
        <label className="flex flex-col gap-1 text-xs text-text-subtle">
          {t.campana}
          <select
            value={campana}
            onChange={(e) => setCampana(e.target.value)}
            className="h-9 min-w-[200px] rounded-md border border-border-strong bg-surface px-2 text-sm text-text-strong"
          >
            <option value="">{t.todasLasCampanas}</option>
            {campanasQuery.data?.campanas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-subtle">
          {t.desde}
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-subtle">
          {t.hasta}
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9" />
        </label>
        {(desde || hasta || campana) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDesde('');
              setHasta('');
              setCampana('');
            }}
          >
            {t.limpiarFiltro}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="error" className="mt-4">
          {getErrorMessage(error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {isLoading && <p className="mt-6 text-sm text-text-subtle">{t.cargando}</p>}

      <h2 className="mt-6 font-titulos text-lg font-semibold text-text-strong">{t.recontacto.titulo}</h2>
      <p className="text-xs text-text-subtle">{t.recontacto.intro}</p>
      {recontacto.isLoading && <p className="mt-2 text-sm text-text-subtle">{t.cargando}</p>}
      {recontacto.error && (
        <Alert variant="error" className="mt-2">
          {getErrorMessage(recontacto.error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {recontacto.data && (
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Tarjeta titulo={t.recontacto.aContactar} valor={recontacto.data.aContactar} />
          <Tarjeta titulo={t.recontacto.contactados} valor={recontacto.data.contactados} tono="success" />
          <Tarjeta
            titulo={t.recontacto.pendientes}
            valor={recontacto.data.pendientes}
            tono={recontacto.data.pendientes > 0 ? 'warning' : 'neutro'}
          />
          <Tarjeta titulo={t.recontacto.convertidos} valor={recontacto.data.convertidos} />
          <Tarjeta
            titulo={t.recontacto.tasaBloqueo}
            valor={`${String(recontacto.data.tasaBloqueo)}%`}
            tono="danger"
            ayuda={t.recontacto.tasaBloqueoAyuda
              .replace('{bloqueados}', String(recontacto.data.nosBloquearon))
              .replace('{contactados}', String(recontacto.data.contactados))}
          />
          <Tarjeta
            titulo={t.recontacto.losBloqueamos}
            valor={recontacto.data.losBloqueamos}
            ayuda={t.recontacto.losBloqueamosAyuda.replace(
              '{antes}',
              String(recontacto.data.nosBloquearonAntes),
            )}
          />
        </div>
      )}

      <h2 className="mt-8 font-titulos text-lg font-semibold text-text-strong">{t.app.titulo}</h2>
      <p className="text-xs text-text-subtle">{t.app.intro}</p>
      {data && (
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Tarjeta titulo={t.tarjetas.mensajesEnviados} valor={data.mensajesEnviados} />
          <Tarjeta titulo={t.tarjetas.mensajesRecibidos} valor={data.mensajesRecibidos} />
          <Tarjeta
            titulo={t.tarjetas.tasaRespuesta}
            valor={`${String(data.tasaRespuesta)}%`}
            tono="success"
            ayuda={t.tarjetas.tasaRespuestaAyuda
              .replace('{respondieron}', String(data.leadsQueRespondieron))
              .replace('{contactados}', String(data.leadsContactados))}
          />
          <Tarjeta
            titulo={t.tarjetas.yaContactados}
            valor={data.leadsYaContactados}
            tono="success"
            ayuda={t.tarjetas.deTotal.replace('{total}', String(data.totalLeads))}
          />
          <Tarjeta
            titulo={t.tarjetas.pendientes}
            valor={data.leadsPendientes}
            tono={data.leadsPendientes > 0 ? 'warning' : 'neutro'}
            ayuda={t.tarjetas.deTotal.replace('{total}', String(data.totalLeads))}
          />
          <Tarjeta
            titulo={t.tarjetas.tasaBloqueo}
            valor={`${String(data.tasaBloqueo)}%`}
            tono="danger"
            ayuda={t.tarjetas.tasaBloqueoAyuda
              .replace('{bloqueados}', String(data.leadsBloqueados))
              .replace('{total}', String(data.totalLeads))}
          />
        </div>
      )}
    </div>
  );
}

function Tarjeta({
  titulo,
  valor,
  ayuda,
  tono = 'neutro',
}: {
  titulo: string;
  valor: string | number;
  ayuda?: string;
  tono?: 'neutro' | 'success' | 'warning' | 'danger';
}): JSX.Element {
  return (
    <div className="rounded-md border border-marca-neutro bg-surface p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">{titulo}</p>
      <p
        className={cn(
          'mt-1 text-3xl font-bold tabular-nums',
          tono === 'success' && 'text-success',
          tono === 'warning' && 'text-warning',
          tono === 'danger' && 'text-danger',
          tono === 'neutro' && 'text-text-strong',
        )}
      >
        {valor}
      </p>
      {ayuda && <p className="mt-1 text-xs text-text-faint">{ayuda}</p>}
    </div>
  );
}
